import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Award, Search, Plus, Edit, Trash2, Loader2 } from "lucide-react";

interface CredentialType {
  id: number;
  name: string;
  description: string | null;
  agency: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  serviceTypeIds?: string[];
}

interface ServiceType {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  isActive: boolean;
}

export default function CredentialTypesPage() {
  usePageTitle("Credential Types");
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCredentialType, setSelectedCredentialType] = useState<CredentialType | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    agency: "",
    isActive: true,
  });
  const [selectedServiceTypeIds, setSelectedServiceTypeIds] = useState<string[]>([]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
    }
  }, [isAuthenticated, authLoading, toast]);

  const isAdmin = currentUser?.role === "admin";

  const { data: credentialTypes = [], isLoading } = useQuery<CredentialType[]>({
    queryKey: ["/api/credential-types"],
    enabled: isAuthenticated,
    refetchOnMount: "always",
  });

  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ["/api/service-types"],
    enabled: isAuthenticated,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData & { serviceTypeIds: string[] }) => {
      return await apiRequest("POST", "/api/credential-types", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/credential-types"] });
      setIsCreateModalOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Credential type created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create credential type",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: typeof formData & { serviceTypeIds: string[] } }) => {
      return await apiRequest("PUT", `/api/credential-types/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/credential-types"] });
      setIsEditModalOpen(false);
      setSelectedCredentialType(null);
      resetForm();
      toast({
        title: "Success",
        description: "Credential type updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update credential type",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest("DELETE", `/api/credential-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/credential-types"] });
      setDeleteId(null);
      toast({
        title: "Success",
        description: "Credential type deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete credential type",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      agency: "",
      isActive: true,
    });
    setSelectedServiceTypeIds([]);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleEdit = (credentialType: CredentialType) => {
    setSelectedCredentialType(credentialType);
    setFormData({
      name: credentialType.name,
      description: credentialType.description || "",
      agency: credentialType.agency || "",
      isActive: credentialType.isActive,
    });
    setSelectedServiceTypeIds(credentialType.serviceTypeIds || []);
    setIsEditModalOpen(true);
  };

  const handleSubmit = () => {
    const dataWithServiceTypes = { ...formData, serviceTypeIds: selectedServiceTypeIds };
    if (selectedCredentialType) {
      updateMutation.mutate({ id: selectedCredentialType.id, data: dataWithServiceTypes });
    } else {
      createMutation.mutate(dataWithServiceTypes);
    }
  };

  const filteredCredentialTypes = credentialTypes.filter((ct) =>
    ct.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ct.agency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ct.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || isLoading) {
    return (
      <ResponsiveLayout title="Credential Types">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </ResponsiveLayout>
    );
  }

  if (!isAdmin) {
    return (
      <ResponsiveLayout title="Credential Types">
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">
              You do not have permission to view this page. Only administrators can manage credential types.
            </p>
          </CardContent>
        </Card>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout
      title="Credential Types"
      subtitle="Manage credential types that can be associated with services"
      icon={<Award className="w-6 h-6 text-primary" />}
      actions={
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Add Credential Type
        </Button>
      }
    >
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search credential types..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCredentialTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? "No credential types match your search." : "No credential types yet. Create your first one!"}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCredentialTypes.map((ct) => (
                  <TableRow key={ct.id}>
                    <TableCell className="font-medium">{ct.name}</TableCell>
                    <TableCell>{ct.agency || "-"}</TableCell>
                    <TableCell className="max-w-xs truncate">{ct.description || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={ct.isActive ? "default" : "secondary"}>
                        {ct.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(ct)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(ct.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isCreateModalOpen || isEditModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedCredentialType(null);
          resetForm();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedCredentialType ? "Edit Credential Type" : "Create Credential Type"}
            </DialogTitle>
            <DialogDescription>
              {selectedCredentialType
                ? "Update the credential type details below."
                : "Add a new credential type that can be associated with services."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Operating Authority"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agency">Agency</Label>
              <Input
                id="agency"
                placeholder="e.g., FMCSA, DOT, State DMV"
                value={formData.agency}
                onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this credential type..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Services</Label>
                <span className="text-sm text-gray-500">Optional</span>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Select the services this credential applies to:
                </p>
                <div className="border rounded-md p-3 max-h-32 overflow-y-auto">
                  {serviceTypes.filter(st => st.isActive).length === 0 ? (
                    <p className="text-sm text-gray-500">No services available</p>
                  ) : (
                    serviceTypes
                      .filter(st => st.isActive)
                      .map((serviceType) => (
                        <div key={serviceType.id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            id={`service-type-${serviceType.id}`}
                            checked={selectedServiceTypeIds.includes(serviceType.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedServiceTypeIds([...selectedServiceTypeIds, serviceType.id]);
                              } else {
                                setSelectedServiceTypeIds(selectedServiceTypeIds.filter(id => id !== serviceType.id));
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <label 
                            htmlFor={`service-type-${serviceType.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            <span className="font-medium">{serviceType.name}</span>
                            {serviceType.category && (
                              <span className="text-gray-500 ml-2">({serviceType.category})</span>
                            )}
                          </label>
                        </div>
                      ))
                  )}
                </div>
                {selectedServiceTypeIds.length > 0 && (
                  <p className="text-sm text-blue-600">
                    {selectedServiceTypeIds.length} service(s) selected
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
                setSelectedCredentialType(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {selectedCredentialType ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credential Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this credential type? This action cannot be undone.
              Any services using this credential will lose the association.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ResponsiveLayout>
  );
}
